import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../api/axiosInstance';
import './SearchBar.css';

interface SearchBarProps {
  onSearch: (query: string) => void;
  initialValue?: string;
  placeholder?: string;
  className?: string;
  debounceTime?: number;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  initialValue = '',
  placeholder = '검색(제목, 내용, 태그)',
  className = '',
  debounceTime = 300
}) => {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<{ id: number; title: string }[]>([]);
  const [history, setHistory] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);

  // 로컬스토리지에서 최근 검색어 로드
  useEffect(() => {
    const savedHistory = localStorage.getItem('searchHistory');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (e) {
        setHistory([]);
      }
    }
  }, []);

  // 외부 클릭 감지하여 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // initialValue 외부 변경 대응
  useEffect(() => {
    if (!isInitialMount.current) {
      setQuery(initialValue);
    } else {
      isInitialMount.current = false;
    }
  }, [initialValue]);

  // 검색 제안 (Suggest) API 호출 디바운스
  const fetchSuggestions = (val: string) => {
    if (!val.trim()) {
      setSuggestions([]);
      return;
    }
    axiosInstance
      .get(`/api/contents/suggest`, { params: { q: val } })
      .then(res => {
        setSuggestions(res.data);
      })
      .catch(err => {
        console.error('검색 자동완성 오류:', err);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowDropdown(true);
    setActiveIndex(-1);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      onSearch(value);
      fetchSuggestions(value);
    }, debounceTime);
  };

  // 최근 검색어 기록 추가
  const saveSearchToHistory = (searchVal: string) => {
    const trimmed = searchVal.trim();
    if (!trimmed) return;
    
    setHistory(prev => {
      const filtered = prev.filter(item => item !== trimmed);
      const updated = [trimmed, ...filtered].slice(0, 8); // 최대 8건 저장
      localStorage.setItem('searchHistory', JSON.stringify(updated));
      return updated;
    });
  };

  const handleSearchTrigger = (searchVal: string) => {
    setQuery(searchVal);
    onSearch(searchVal);
    saveSearchToHistory(searchVal);
    setShowDropdown(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    if (showDropdown && activeIndex >= 0 && suggestions[activeIndex]) {
      handleSearchTrigger(suggestions[activeIndex].title);
    } else {
      handleSearchTrigger(query);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleRemoveHistoryItem = (e: React.MouseEvent, itemToRemove: string) => {
    e.stopPropagation();
    setHistory(prev => {
      const updated = prev.filter(item => item !== itemToRemove);
      localStorage.setItem('searchHistory', JSON.stringify(updated));
      return updated;
    });
  };

  const handleClearAllHistory = (e: React.MouseEvent) => {
    e.stopPropagation();
    setHistory([]);
    localStorage.removeItem('searchHistory');
  };

  return (
    <div className="search-bar-container" ref={searchContainerRef}>
      <form onSubmit={handleSubmit} className={`search-bar-form ${className}`}>
        <div className="search-input-wrapper">
          <input
            type="text"
            value={query}
            onChange={handleChange}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="modern-input search-text-input"
          />
          
          {showDropdown && (query.trim().length > 0 || history.length > 0) && (
            <div className="search-dropdown-overlay">
              {query.trim().length > 0 ? (
                // 자동완성 제안
                <div className="suggestions-section">
                  {suggestions.length > 0 ? (
                    <ul className="suggestions-list-box">
                      {suggestions.map((item, index) => (
                        <li
                          key={item.id}
                          onClick={() => handleSearchTrigger(item.title)}
                          className={`suggestion-list-item ${index === activeIndex ? 'active' : ''}`}
                          onMouseEnter={() => setActiveIndex(index)}
                        >
                          🔍 {item.title}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="no-suggestion-text">추천 검색어가 없습니다.</div>
                  )}
                </div>
              ) : (
                // 최근 검색어 기록
                <div className="history-section">
                  <div className="history-header">
                    <span>최근 검색어</span>
                    <button type="button" onClick={handleClearAllHistory} className="clear-all-btn">
                      전체 삭제
                    </button>
                  </div>
                  <ul className="history-list">
                    {history.map((item, idx) => (
                      <li
                        key={idx}
                        onClick={() => handleSearchTrigger(item)}
                        className="history-list-item"
                      >
                        <span>🕒 {item}</span>
                        <button
                          type="button"
                          onClick={(e) => handleRemoveHistoryItem(e, item)}
                          className="delete-history-btn"
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="modern-btn modern-btn-primary search-submit-btn"
          disabled={!query.trim()}
        >
          🔍 검색
        </button>
      </form>
    </div>
  );
};

export default SearchBar;